/**
 * Flavor System
 * 
 * Central registry and utilities for managing UI flavors.
 * This module provides:
 * - Flavor registry with all available flavors
 * - Utility functions for flavor management
 * - React hook for accessing current flavor
 */

import { useMemo } from 'react';
import { config } from '../config';
import type { FlavorConfig, FlavorId, FlavorRegistry, FlavorStyles, UseFlavorResult } from './types';

// Import flavor configurations
import flavorAConfig from './flavor-a';
import flavorBConfig from './flavor-b';
import flavorCConfig from './flavor-c';

/**
 * Flavor Registry
 * Maps flavor IDs to their configurations
 */
export const FLAVORS: FlavorRegistry = {
  A: flavorAConfig,
  B: flavorBConfig,
  C: flavorCConfig,
} as const;

/**
 * Default flavor to use when configuration is invalid
 */
export const DEFAULT_FLAVOR: FlavorConfig = flavorAConfig;

/**
 * Get flavor configuration by ID
 * Returns default flavor if ID is invalid
 */
export const getFlavorConfig = (flavorId?: string): FlavorConfig => {
  if (!flavorId) return DEFAULT_FLAVOR;
  
  const uppercaseId = flavorId.toUpperCase();
  const flavor = FLAVORS[uppercaseId as FlavorId];
  
  if (!flavor) {
    console.warn(`[Flavors] Unknown flavor ID: ${flavorId}. Using default flavor A.`);
    return DEFAULT_FLAVOR;
  }
  
  return flavor;
};

/**
 * Get current flavor from configuration
 */
export const getCurrentFlavor = (): FlavorConfig => {
  return getFlavorConfig(config.TRANSFER_FLAVOR);
};

/**
 * Get all available flavor IDs
 */
export const getAvailableFlavorIds = (): FlavorId[] => {
  return Object.keys(FLAVORS) as FlavorId[];
};

/**
 * Check if a flavor ID is valid
 */
export const isValidFlavorId = (flavorId: string): boolean => {
  return flavorId.toUpperCase() in FLAVORS;
};

/**
 * React Hook: Use Flavor
 * 
 * Provides access to the current flavor configuration and utilities
 * 
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { className, isFlavor, getStyle } = useFlavor();
 *   
 *   return (
 *     <div className={`my-component ${className}`}>
 *       {isFlavor('B') && <SpecialFeature />}
 *       <div style={{ borderRadius: getStyle('borderRadius') }}>
 *         Content
 *       </div>
 *     </div>
 *   );
 * };
 * ```
 */
export const useFlavor = (): UseFlavorResult => {
  const currentFlavor = useMemo(() => getCurrentFlavor(), []);
  
  const isFlavor = (flavorId: FlavorId): boolean => {
    return currentFlavor.id === flavorId;
  };
  
  const getStyle = <K extends keyof FlavorStyles>(
    key: K
  ): FlavorStyles[K] | undefined => {
    return currentFlavor.styles?.[key];
  };
  
  return {
    config: currentFlavor,
    id: currentFlavor.id,
    className: currentFlavor.className,
    isFlavor,
    getStyle,
  };
};


/**
 * Utility: Get flavor className string
 * Useful for inline className usage
 */
export const getFlavorClassName = (): string => {
  return getCurrentFlavor().className;
};

/**
 * Utility: Check if current flavor matches given ID
 * Useful for conditional logic outside React components
 */
export const isCurrentFlavor = (flavorId: FlavorId): boolean => {
  return getCurrentFlavor().id === flavorId;
};

// Export types
export type { FlavorConfig, FlavorId, FlavorRegistry, UseFlavorResult } from './types';
